/**
 * ConfirmLogoutModal.tsx
 *
 * File-level:
 * - Small, reusable confirmation modal used to confirm admin logout actions.
 *
 * Notes:
 * - Implements a lightweight, accessible modal without depending on external dialog primitives.
 * - Single responsibility: only renders a confirmation UI and emits confirm/close events.
 */

import React, { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

export interface ConfirmLogoutModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the user confirms the logout action */
  onConfirm: () => void;
  /** Called when the user cancels/closes the modal */
  onClose: () => void;
}

/**
 * ConfirmLogoutModal
 *
 * @description Presents a confirmation dialog asking the admin to confirm logout.
 * - Handles Escape key to close.
 * - Clicking the backdrop will close the dialog.
 */
export default function ConfirmLogoutModal({ open, onConfirm, onClose }: ConfirmLogoutModalProps) {
  useEffect(() => {
    /**
     * handleKey
     *
     * @param e KeyboardEvent
     * @description Close the modal when Escape is pressed.
     */
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }

    if (open) {
      document.addEventListener('keydown', handleKey);
      // Prevent background scrolling while modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm logout"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-rose-100 rounded-lg shadow-lg border overflow-hidden">
          <div className="p-6 flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-700 dark:!text-green-600">Confirm Logout</h3>
              <p className="text-sm text-slate-600 mt-1">
                Are you sure you want to log out of the admin console? You will be redirected to the Home page.
              </p>

              <div className="mt-4 flex justify-end gap-3">
                <Button variant="outline" className="bg-blue-600" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={onConfirm}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
